// Server-authoritative automation player-CHOICE picker (P6 cutover, Stage B).
//
// When a delegated automation op needs a target, the SERVER writes a durable
// `pendingChoice` onto MatchState (candidates = public board instanceIds) and
// DEFERS. This module folds that prompt into a local `pendingServerChoice` slot
// for the CHOOSER's client, drives the existing target-halo highlight off it
// (getEquipTargetIds), and turns the next click on a candidate into a
// `chooseIntent` (the server validates + resumes + clears). Kept isolated from
// the local `pendingTarget` path so the legacy P2P target flow stays intact.
import { chooseIntent, chooseCellIntent, choosePileIntent } from '../../../utils/game/net/automationIntents';
import { castFromDeckTopIntent } from '../../../utils/game/net/castIntents';
import { startPileMultiSelect } from './pileMultiSelect';
import { offerBladedancerAttack } from './avatarAbilities';
import { foldHarbingerPromptFromMirror } from './harbingerCeremony';
import { foldDragonlordPromptFromMirror } from './avatarAbilities';
import { foldNameSpellPromptFromMirror } from './nameSpellPrompt';
import { foldOraclePromptFromMirror } from './oracleReading';
import { foldBattlemagePromptFromMirror } from './battlemagePledge';
import { t } from '../../../localization/t';

// Fold the authoritative pendingChoice → local picker state. Only the chooser
// (the seat the prompt is addressed to) drives a picker; the opponent's prompt
// folds to null (they just see the board, optionally a passive "X is choosing").
// Idempotent: no setState when the active prompt id is unchanged.
export function applyPendingChoiceFromMirror(host, mirrorState) {
  // Replay: a recorded pendingChoice is history, not a question for the
  // viewer. Never fold it into a picker (target halos, pile dialogs, discard
  // glow, modal drawers all present themselves as answerable).
  if (host.props?.replayDriver) return;
  const pc = mirrorState?.pendingChoice || null;
  // Harbinger pre-game ceremony: public choreography rendered by the ceremony
  // overlay on BOTH seats, never the generic pickers.
  if (foldHarbingerPromptFromMirror(host, pc)) return;
  // Dragonlord pre-game set-aside: rendered through the pile-search picker for
  // the chooser (display pool narrowed to the owned collection), never the
  // generic modal.
  if (foldDragonlordPromptFromMirror(host, pc)) return;
  // Oracle reading at setup: the omen overlay for the chooser, a wait for
  // the other seat. Never the generic modal.
  if (foldOraclePromptFromMirror(host, pc)) return;
  // Battlemage pledge at setup: the same shape, its own overlay.
  if (foldBattlemagePromptFromMirror(host, pc)) return;
  // Feast for Crows' name-a-spell pick: rendered through the searchable pile
  // picker for the chooser (the choices span the whole spell catalog), never
  // the generic modal.
  if (foldNameSpellPromptFromMirror(host, pc)) return;
  const localSeat = host._localSeat?.();
  // Solo: the pilot is BOTH seats. Every prompt is theirs to answer (the
  // answer intents carry pc.seat, so the acting seat stays the addressee).
  const active = pc && (host.props?.isCasualSandbox || pc.seat === localSeat) ? pc : null;
  const cur = host.state.pendingServerChoice || null;
  if (!active && !cur) return;
  if (active && cur && cur.promptId === active.promptId) {
    // Same prompt, but its PICKER may have failed to open (a reconnect Full
    // folds the prompt BEFORE the deferred board/pile rebuild, so a
    // 'board'/'collection' session found no cards; or a mandatory dialog was
    // ESC'd. The server no-ops the decline and never sends a clearing diff).
    // Re-attempt on every fold; openServerPileSession is idempotent behind
    // pendingPileSelect, and the hand glow behind _pendingDiscardCost.
    if (active.kind === 'pile') {
      if (active.zone === 'hand') {
        if (!host._pendingDiscardCost) stageServerHandDiscard(host, active);
      } else if (!host.state.pendingPileSelect) {
        openServerPileSession(host, active);
      }
    }
    return;
  }
  host.setState({
    pendingServerChoice: active
      ? {
          promptId: active.promptId,
          seat: active.seat,
          kind: active.kind || 'target',
          candidates: [...(active.candidates || [])],
          cells: [...(active.cells || [])],
          choices: [...(active.choices || [])],
          optional: !!active.optional,
          // Scry: the modal shows this card (by id. The chooser's own pile
          // top, identity already known locally) above the buttons.
          display: active.display || null,
          description: active.description || null,
          min: active.min ?? 0,
          max: active.max ?? (active.candidates || []).length,
          // Cast-from-top Summon affordance (Cradle of Etherrum): a local UX
          // annotation on the server's LOOK modal. The actual cast is a
          // server-validated fromDeckTop intent.
          castOption: active.kind === 'modal'
            ? (host._buildServerScryCastOption?.(active) || null) : null,
          title: active.title || null,
          confirmLabel: active.confirmLabel || null,
          pair: !!active.pair,
          zone: active.zone || null,
          tag: active.tag || null,
          // Provenance for the storyline surface. Newer servers hoist the
          // resume's sourceId to the top level; fall back to reading it off
          // the resume directly (projected verbatim) for older ones.
          sourceId: active.sourceId || active.resume?.sourceId || null,
        }
      : null,
  }, () => {
    // A PILE prompt renders through the existing pile multi-select dialog
    // (memory: reuse the pile-view UI, never a bespoke picker). Open the
    // cemetery dialog spanning both sides; the allowlist is the server's
    // candidate set and the confirm/cancel both reply via choosePileIntent
    // (ESC = an empty commit. The server's min is 0 for up-to-N prompts).
    if (!active && host._pendingDiscardCost?.kind === 'server_choice') {
      // The server prompt resolved/cleared elsewhere. Drop the stale hand glow.
      host._pendingDiscardCost = null;
      host.forceUpdate();
    }
    if (active?.kind === 'pile' && active.zone === 'hand') {
      // A HAND pick (Wizard's Den's discard) rides the existing discard-cost
      // hand glow instead of the pile dialog; resolveDiscardCostPick sends the
      // choosePileIntent reply for the 'server_choice' kind. Re-stage on a
      // promptId change: when the server clears prompt A and pops queued
      // prompt B in the same diff, a null-only guard would pin A's stale
      // candidate set (and promptId) for the rest of the match.
      if (host._pendingDiscardCost?.promptId !== active.promptId) stageServerHandDiscard(host, active);
    } else if (active?.kind === 'pile' && !host.state.pendingPileSelect) {
      openServerPileSession(host, active);
    }
  });
}

// Avatar of Fire: rebuild the local ignite UX flag (hand glow + the
// castFireSiteAsFireball gate) from the server's _fireballIgnite window on a
// reconnect Full. The authoritative window survives the reload; without this
// re-derive the player couldn't cast further Fireballs after reconnecting.
export function applyIgniteWindowFromMirror(host, mirrorState) {
  const cards = mirrorState?.cards;
  if (!cards) return;
  for (const [id, mc] of Object.entries(cards)) {
    const ignite = mc?._fireballIgnite;
    if (!ignite || mc.zone !== 'play') continue;
    const controller = mc.rotated ? 'p2' : 'p1';
    const turnNumber = mirrorState?.phase?.turnNumbers?.[controller] ?? null;
    if (ignite.turnNumber !== turnNumber) continue; // stale window
    if (!host._avatarOfFireIgnite) host._avatarOfFireIgnite = {};
    host._avatarOfFireIgnite[controller] = { turnNumber: ignite.turnNumber, avatarCardId: id };
  }
}

function stageServerHandDiscard(host, pc) {
  host._pendingDiscardCost = {
    controller: pc.seat,
    sourceCardId: null,
    kind: 'server_choice',
    // The server already narrowed the eligible hand cards (e.g. Sisters of
    // Avalon excludes Sites, Ignis Rex allows fire only); glow and accept
    // exactly that set (isEligibleDiscardPick). No filter is staged: with a
    // missing or empty candidate list (no current server sends either for a
    // hand prompt) eligibility falls back to match-everything, which keeps a
    // mandatory prompt answerable instead of unanswerable.
    candidateIds: pc.candidates?.length ? new Set(pc.candidates) : null,
    promptId: pc.promptId,
  };
  host.forceUpdate();
}

function openServerPileSession(host, pc) {
  // The CHOOSER's side, from the prompt itself. Online pc.seat is always the
  // local seat (only the addressee opens a session); in solo the pilot answers
  // either seat's prompt, so the session must anchor on the prompt's seat.
  const localRotated = pc.seat === 'p2';
  let own; let piles;
  if (pc.zone === 'collection') {
    // Erik's Curiosa: the pick is over the chooser's OWN collection.
    own = (host.state.gameState?.piles || []).find(
      (p) => p.name === 'Collection' && !!p.rotated === localRotated,
    );
    piles = [own].filter(Boolean);
  } else if (pc.zone === 'spellbook_window') {
    // Black Mass: render ONLY the server's window ids over the own spellbook.
    // a synthetic pile sharing the real spellbook's id (dialog pattern the
    // local Black Mass flow already uses).
    const sb = (host.state.gameState?.piles || []).find(
      (p) => p.name === 'Spellbook' && !!p.rotated === localRotated,
    );
    if (!sb) return;
    const byId = new Map(sb.cards.map((c) => [c.id, c]));
    const windowCards = (pc.window || []).map((id) => byId.get(id)).filter(Boolean);
    own = { ...sb, cards: windowCards };
    piles = [own];
  } else if (pc.zone === 'board') {
    // Flanking Maneuver's subset stage: a virtual pile over BOARD card
    // instances (the server's candidate ids). The same dialog pattern the
    // local flanking flow uses ('flanking-subset').
    const cards = (pc.candidates || []).map((id) => host._findCardById(id)).filter(Boolean);
    if (cards.length === 0) return;
    own = { id: 'server-board-subset', name: 'Choose', cards, infinite: true };
    piles = [own];
  } else {
    own = host.findOrCreateCemetery(localRotated);
    const opp = host.findOrCreateCemetery(!localRotated);
    piles = [own, opp].filter(Boolean);
  }
  if (piles.length === 0 || !own) return;
  startPileMultiSelect(host, own, {
    kind: 'server_pile_choice',
    controller: pc.seat,
    sourceCardId: null,
    sourceName: 'Effect',
    title: pc.title || t('Choose cards'),
    hint: t('Click to select. Right-click or click again to unselect'),
    confirmLabel: pc.confirmLabel || t('Confirm'),
    max: pc.max ?? (pc.candidates || []).length,
    minSelect: pc.min ?? 0,
    eligibleIds: [...(pc.candidates || [])],
    pileIds: piles.map((p) => p.id),
    spanCemeteries: true,
    meta: { promptId: pc.promptId, seat: pc.seat, pair: !!pc.pair, distinctNames: !!pc.distinctNames },
  });
}

// The storyline surface for a server CELL/TARGET pick. The board halos take
// the actual click, but without a drawer entry the player has no idea WHY
// cells lit up (e.g. Geomancer's earth-site Rubble fill). Synthesize the same
// pick_cell / pick_target prompt shape the local startCellPick path pushes,
// with the source card resolved from the prompt's sourceId. A mandatory pick
// hides Cancel (declineServerChoice would no-op anyway).
export function buildServerPickPrompt(host, pc) {
  const source = resolveSourceInstance(host, pc.sourceId);
  return {
    id: pc.promptId,
    promptType: pc.kind === 'cell' ? 'pick_cell' : 'pick_target',
    description: pc.description
      || (pc.kind === 'cell' ? t('Choose a location') : t('Choose a target')),
    cardInstance: source,
    cardName: source?.name || t('Effect'),

    noCancel: !pc.optional,
  };
}

// Resolve a prompt's source card id against every place its instance can live
// client-side: board meshes (incl. carried relics), piles, hands, and a
// mid-flight storyline cast (castingSpells carries name + art only).
function resolveSourceInstance(host, id) {
  if (!id) return null;
  const inPlay = host._inPlayInstanceById?.(id);
  if (inPlay) return inPlay;
  const pileCard = (host.state.gameState?.piles || [])
    .flatMap((p) => p.cards || []).find((c) => c.id === id);
  if (pileCard) return pileCard;
  const handCard = [...(host.state.handCards || []), ...(host.state.handCards2 || [])]
    .find((c) => c.id === id);
  if (handCard) return handCard;
  const casting = (host.state.castingSpells || []).find((f) => f.id === id);
  if (casting) return { id: casting.id, imageUrl: casting.imageUrl, name: casting.cardName };
  return null;
}

// The server_pile_choice session's reply. Shared by confirm (picked ids) and
// cancel (empty commit). Clears the local picker; the server's
// pendingChoice→null diff confirms.
export function resolveServerPileChoice(host, session, ids) {
  host._sendMatchIntent(choosePileIntent(session.meta.promptId, ids, session.meta.seat));
  host.setState({ pendingServerChoice: null, pendingPileSelect: null, searchPile: null });
}

// Click resolution: a click on a candidate sends the choose intent. Returns true
// when the click was consumed. The local picker is cleared optimistically. The
// server's pendingChoice→null diff confirms it (and the effect's own diff
// renders). A stale/illegal pick is a server no-op; the next fold re-shows the
// still-open prompt.
export function tryResolveServerChoice(host, clickedCard) {
  const pc = host.state.pendingServerChoice;
  if (!pc || !clickedCard) return false;
  if (!pc.candidates.includes(clickedCard.id)) return false;
  host._sendMatchIntent(chooseIntent(pc.promptId, clickedCard.id, pc.seat));
  host.setState({ pendingServerChoice: null });
  return true;
}

// CELL-pick resolution: a click on the table resolves to a cell; if it's in the
// server's legal set, send chooseCellIntent. Mirror of tryResolveServerChoice for
// kind:'cell' (raycast → cell → validate → intent). Returns true if consumed.
export function tryResolveServerCellChoice(host, event) {
  const pc = host.state.pendingServerChoice;
  if (!pc || pc.kind !== 'cell') return false;
  const point = host.scene?.raycastTablePoint(event);
  if (!point) return false;
  const cell = host._cellAtWorld(point.x, point.z);
  if (!cell) return false;
  if (!pc.cells.includes(`${cell.col},${cell.row}`)) return false;
  host._sendMatchIntent(chooseCellIntent(pc.promptId, cell, pc.seat));
  host.setState({ pendingServerChoice: null });
  // Bladedancer: the server commits the step off the intent; the follow-up
  // attack is a normal client combat declaration at the destination (the
  // targets there don't depend on her not-yet-folded position).
  if (pc.tag === 'bladedancer_step' && pc.sourceId) {
    const avatar = host._findCardById(pc.sourceId);
    if (avatar) offerBladedancerAttack(host, avatar, cell);
  }
  return true;
}

// MODAL-pick resolution: the modal picker button click sends the picked value.
// The server validates it against the recorded choices, resumes, and clears.
// Mirror of tryResolveServerChoice for kind:'modal'. Returns true if consumed.
export function resolveServerModalChoice(host, value) {
  const pc = host.state.pendingServerChoice;
  if (!pc || pc.kind !== 'modal') return false;
  // Cast-from-top Summon (Cradle of Etherrum): a CLIENT-appended choice on the
  // server's LOOK modal. Close the look (the standing grant is independent of
  // it), then commit via the server-validated fromDeckTop cast intent. The
  // summoned card + deck pop + mana debit all fold from the diff.
  if (value === '__cast_from_top' && pc.castOption) {
    const { topCardId, cell } = pc.castOption;
    host._sendMatchIntent(chooseIntent(pc.promptId, 'top', pc.seat));
    host._sendMatchIntent(castFromDeckTopIntent(topCardId, pc.seat, {
      col: cell.col, row: cell.row,
    }));
    host.setState({ pendingServerChoice: null });
    return true;
  }
  if (!(pc.choices || []).some((c) => c.value === value)) return false;
  host._sendMatchIntent(chooseIntent(pc.promptId, value, pc.seat));
  host.setState({ pendingServerChoice: null });
  return true;
}

// Decline an OPTIONAL ("may") choice. ESC sends a null pick the server clears
// without resuming. A mandatory prompt ignores this (the picker stays up).
// Works for both target + cell prompts (the server reads choiceId/cell = null).
export function declineServerChoice(host) {
  const pc = host.state.pendingServerChoice;
  if (!pc || !pc.optional) return false;
  host._sendMatchIntent(pc.kind === 'cell'
    ? chooseCellIntent(pc.promptId, null, pc.seat)
    : chooseIntent(pc.promptId, null, pc.seat));
  host.setState({ pendingServerChoice: null });
  return true;
}
